import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const WORK_PERMIT_TASK_ID = 40;

const WorkPermitForm = () => {
  const navigate = useNavigate();
  const { id: paramId, wpId } = useParams();
  const isNew = !wpId || wpId === "new";

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [expiryDate, setExpiryDate] = useState("");
  const [file, setFile] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [createdDate, setCreatedDate] = useState(null);
  const [isSigned, setIsSigned] = useState(false);

  useEffect(() => {
    if (!isNew) {
      const fetchPermit = async () => {
        try {
          const res = await axios.get(
            `${API_BASE}/api/workpermit/detail/${wpId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const data = res.data;
          setExpiryDate(
            data.wp_expiry_date ? data.wp_expiry_date.split("T")[0] : ""
          );
          setUploadedFileName(data.wp_file);
          setOriginalFile(data.wp_original);
          setCreatedDate(data.wp_created_date);
        } catch (err) {
          console.error("❌ Fetch failed", err);
        }
      };
      fetchPermit();
    }
  }, [isNew, wpId]);

  useEffect(() => {
    const fetchSignStatus = async () => {
      if (!wpId || wpId === "new") return;

      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const refKey = `40_${wpId}`; // 40 = Work Permit task id
        const taskKey = "work_permit";
        const status =
          res.data.status?.[refKey] ??
          res.data.status?.[taskKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("❌ Failed to fetch sign status", err);
      }
    };

    fetchSignStatus();
  }, [userId, wpId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file && isNew) return alert("Please upload a PDF file!");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("expiry_date", expiryDate);
    formData.append("userId", userId);

    try {
      if (isNew) {
        await axios.post(
          `${API_BASE}/api/workpermit?userId=${userId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        alert("✔️ Work permit created successfully!");
      } else {
        await axios.put(
          `${API_BASE}/api/workpermit/${wpId}?userId=${userId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        alert("✔️ Work permit updated successfully!");
      }

      // ✅ Redirect back to WorkPermitList after successful save
      navigate(`/workpermit/${userId}`);
    } catch (err) {
      console.error("❌ Save failed", err);
      alert("Upload failed. Please try again.");
    }
  };

  const handleSignComplete = async (isCompleted) => {
    if (!isCompleted) return; // only act when signed
    try {
      await axios.put(
        `${API_BASE}/api/workpermit/complete/${wpId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("✅ Latest Work Permit marked as review");
    } catch (err) {
      console.error("❌ Failed to mark latest Work Permit as review", err);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Work Permit</span>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Return
        </button>
      </div>

      {/* Form Section */}
      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-4 text-start">
            {/* Left side can stay empty or hold something else */}
          </div>
          <div className="col-4 text-center">
            <h3 className="mb-0">Work Permit</h3>
          </div>

          <div className="col-4 text-end">
            <div>New‑Way Trucking Ltd</div>
            <div>7725 48 St. SE, Calgary</div>
            <div>Alberta, Canada T2C 2V3</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-4">
          <div className="col-md-3 mb-3">
            <label>Expiry Date</label>
            <input
              type="date"
              className="form-control"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              disabled={!isAdmin && isSigned}
            />
          </div>

          <div className="mb-3">
            <label>Select PDF File *</label>
            <input
              type="file"
              className="form-control"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files[0])}
              disabled={!isAdmin && isSigned}
            />
          </div>

          {originalFile && (
            <div className="p-3 bg-light rounded border">
              <div className="text-muted mb-2">Uploaded File:</div>
              <a
                href={`${API_BASE}/uploads/workpermits/${uploadedFileName}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: "bold", textDecoration: "underline" }}
              >
                {originalFile}
              </a>
              {createdDate && (
                <div className="text-muted mt-1">
                  Uploaded: {new Date(createdDate).toLocaleString()}
                </div>
              )}
            </div>
          )}

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary mt-3"
              disabled={!isAdmin && isSigned}
            >
              Save
            </button>
          </div>
        </form>

        {/* Signature & Review Section */}

        <hr className="my-4" />
        <SignatureManager
          taskName="work_permit"
          taskId={WORK_PERMIT_TASK_ID}
          referenceId={wpId}
          userId={userId}
          isAdmin={isAdmin}
          onSignComplete={handleSignComplete}
        />

        <ReviewManager
          taskName="work_permit"
          taskId={WORK_PERMIT_TASK_ID}
          referenceId={wpId}
          userId={userId}
          isAdmin={isAdmin}
        />
      </div>
    </>
  );
};

export default WorkPermitForm;
